export const BRAIN_TEAM_STORY_VISUAL_EFFECT_FAMILIES = [
  'current_flow',
  'arrival_pulse',
  'route_glow',
  'chapter_wash',
] as const;

export type BrainTeamStoryVisualEffectFamily = (typeof BRAIN_TEAM_STORY_VISUAL_EFFECT_FAMILIES)[number];

/**
 * Bounded presentation direction for one Evidence Story.
 *
 * It deliberately carries no code, asset, coordinate, target, or route data.
 * Those primitives are resolved again by the authorized map at render time.
 */
export interface BrainTeamStoryVisualEffect {
  schema_version: 1;
  family: BrainTeamStoryVisualEffectFamily;
  intensity: number;
  motion: number;
  density: number;
}

export const DEFAULT_BRAIN_TEAM_STORY_VISUAL_EFFECT: BrainTeamStoryVisualEffect = {
  schema_version: 1,
  family: 'current_flow',
  intensity: 0.65,
  motion: 0.5,
  density: 0.5,
};

const FAMILY_SET = new Set<string>(BRAIN_TEAM_STORY_VISUAL_EFFECT_FAMILIES);
const EFFECT_KEYS = ['schema_version', 'family', 'intensity', 'motion', 'density'] as const;

const boundedControl = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;

/** Strict canonical parser. A present malformed version owns its field and
 * fails closed at the containing Story parser rather than becoming a legacy
 * or inferred effect. */
export function parseBrainTeamStoryVisualEffect(value: unknown): BrainTeamStoryVisualEffect | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const raw = value as Record<string, unknown>;
  const keys = Object.keys(raw);
  if (
    keys.length !== EFFECT_KEYS.length ||
    keys.some((key) => !EFFECT_KEYS.includes(key as (typeof EFFECT_KEYS)[number]))
  ) {
    return undefined;
  }
  if (
    raw.schema_version !== 1 ||
    typeof raw.family !== 'string' ||
    !FAMILY_SET.has(raw.family) ||
    !boundedControl(raw.intensity) ||
    !boundedControl(raw.motion) ||
    !boundedControl(raw.density)
  ) {
    return undefined;
  }
  return {
    schema_version: 1,
    family: raw.family as BrainTeamStoryVisualEffectFamily,
    intensity: raw.intensity,
    motion: raw.motion,
    density: raw.density,
  };
}

export function createBrainTeamStoryVisualEffect(
  family: BrainTeamStoryVisualEffectFamily,
  controls?: Pick<BrainTeamStoryVisualEffect, 'intensity' | 'motion' | 'density'> | null,
): BrainTeamStoryVisualEffect {
  return {
    ...DEFAULT_BRAIN_TEAM_STORY_VISUAL_EFFECT,
    ...(controls ?? {}),
    family,
  };
}
